import { Button, Card, Group, Loader, Rating, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import { DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generateCSV, getMessageMetrics, getSessionMetrics, useBot } from '@mygpt/app';
import { SvgDownload, SvgInfo } from '@/components/Dashboard/icon';
import DateSelector from '@/components/DateSelector';

export interface OverviewCardProps extends Omit<React.ButtonHTMLAttributes<HTMLDivElement>, 'title'> {
  id?: string;
  value: number;
  time?: string;
  valueUnit?: string;
  title?: string;
  tip?: string;
  unit?: string;
}

const ItemCard: React.FC<OverviewCardProps> = ({ time, tip, title, unit, value, valueUnit, ...props }) => {
  return (
    <Card
      shadow="sm"
      px="sm"
      py="md"
      radius="md"
      className="group transform transition-transform duration-300 hover:shadow-md cursor-pointer"
      {...props}
    >
      <Stack justify="space-between" className="h-full">
        <Stack className="flex-auto flex items-center">
          <Group gap={5} wrap="nowrap">
            <Text className="text-gray-900" size="sm">
              {title}
            </Text>
            {tip && (
              <Tooltip label={tip} withinPortal withArrow>
                <span>
                  <SvgInfo />
                </span>
              </Tooltip>
            )}
          </Group>
          <Text className="text-sm text-[#98A2B3]">{time}</Text>
        </Stack>
        <Stack align="center" className="flex-none text-right mx-2">
          <Text className="text-xl text-gray-800 font-bold">
            {value}
            {valueUnit}
          </Text>
          <Text className="text-gray-600" lh={1.2} size="xs">
            {unit}
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
};

interface MetricsSessionItem {
  day: string;
  evaluate_score_count: number;
  evaluate_score_value: number;
  session_count: number;
}

interface MetricsMessageItem {
  day: string;
  dislike_count: number;
  evaluation_answer_count: number;
  evaluation_source_count: number;
  like_count: number;
  message_count: number;
  unanswerable_count: number;
}

type TotalData = {
  totalSessions: number;
  scoreSessions: number;
  unscoreSessions: number;
  scoreCompleteRate: number;
  totalMessages: number;
  addAnswerToQA: number;
  addSourceToQA: number;
  unanswerable: number;
  answerRate: number;
  score: number;
};

dayjs.extend(utc);

export const Overview = ({
  dateRange,
  onDateRangeChange,
  rangeDateTimeText,
  setRangeDateTimeText,
}: {
  dateRange: DatesRangeValue;
  onDateRangeChange: (date: DatesRangeValue) => void;
  rangeDateTimeText: string;
  setRangeDateTimeText: (text: string) => void;
}) => {
  const { t } = useTranslation();
  const { botId } = useBot();

  const [loading, setLoading] = useState(false);
  const [averageScore, setAverageScore] = useState<string>('0');
  const [metricsSessionData, setMetricsSessionData] = useState<MetricsSessionItem[]>([]);
  const [metricsMessageData, setMetricsMessageData] = useState<MetricsMessageItem[]>([]);

  const CARD_LIST = [
    {
      id: 'totalSessions',
      title: t('chat_history.overview.total_session'),
      value: 0,
      unit: t('chat_history.overview.session_unit'),
      valueUnit: '',
      tip: t('chat_history.overview.total_session_tip'),
    },
    {
      id: 'scoreSessions',
      title: t('chat_history.overview.score_session'),
      value: 0,
      unit: t('chat_history.overview.session_unit'),
      valueUnit: '',
      tip: t('chat_history.overview.score_session_tip'),
    },
    {
      id: 'unscoreSessions',
      title: t('chat_history.overview.unscore_session'),
      value: 0,
      unit: t('chat_history.overview.session_unit'),
      valueUnit: '',
      tip: t('chat_history.overview.unscore_session_tip'),
    },
    {
      id: 'scoreCompleteRate',
      title: t('chat_history.overview.score_rate'),
      value: 0,
      unit: '',
      valueUnit: '%',
      tip: t('chat_history.overview.score_rate_tip'),
    },
    {
      id: 'totalMessages',
      title: t('chat_history.overview.total_message'),
      value: 0,
      unit: t('chat_history.overview.message_unit'),
      valueUnit: '',
      tip: t('chat_history.overview.total_message_tip'),
    },
    {
      id: 'addAnswerToQA',
      title: t('chat_history.overview.qa_answer'),
      value: 0,
      unit: t('chat_history.overview.message_unit'),
      valueUnit: '',
      tip: t('chat_history.overview.qa_answer_tip'),
    },
    {
      id: 'addSourceToQA',
      title: t('chat_history.overview.qa_source'),
      value: 0,
      unit: t('chat_history.overview.message_unit'),
      valueUnit: '',
      tip: t('chat_history.overview.qa_source_tip'),
    },
    {
      id: 'answerRate',
      title: t('chat_history.overview.answer_rate'),
      value: 0,
      unit: '',
      valueUnit: '%',
      tip: t('chat_history.overview.answer_rate_tip'),
    },
  ];

  const cardList = useMemo(() => {
    const data: TotalData = {
      totalSessions: 0,
      scoreSessions: 0,
      unscoreSessions: 0,
      scoreCompleteRate: 0,
      totalMessages: 0,
      addAnswerToQA: 0,
      addSourceToQA: 0,
      unanswerable: 0,
      answerRate: 0,
      score: 0,
    };
    metricsSessionData.map((item) => {
      data.totalSessions += item.session_count;
      data.scoreSessions += item.evaluate_score_count;
      data.score += item.evaluate_score_value;
    });
    data.unscoreSessions = data.totalSessions - data.scoreSessions;
    metricsMessageData.map((item) => {
      data.totalMessages += item.message_count;
      data.addAnswerToQA += item.evaluation_answer_count;
      data.addSourceToQA += item.evaluation_source_count;
      data.unanswerable += item.unanswerable_count;
    });
    data.scoreCompleteRate =
      data.totalSessions === 0 ? 0 : +((data.scoreSessions / data.totalSessions) * 100).toFixed(0);
    data.answerRate =
      data.totalMessages === 0
        ? 0
        : +(((data.totalMessages - data.unanswerable) / data.totalMessages) * 100).toFixed(0);
    setAverageScore(data.scoreSessions > 0 ? (data.score / data.scoreSessions).toFixed(1) : '0');
    const newList = CARD_LIST.concat([]);
    newList.map((item) => {
      item.value = data[item.id as keyof TotalData];
    });
    return newList;
  }, [metricsSessionData, metricsMessageData, t]);

  const rangeDateText = useMemo(() => {
    if (dateRange[0] && dateRange[1]) {
      const text = dateRange.map((date) => dayjs(date).format('YYYY-MM-DD'));
      if (text[0] === text[1]) return text[0];
      return text.join(' ~ ');
    }
  }, [dateRange]);

  const handleDateChange = (date: DatesRangeValue | null, text: string) => {
    if (date) {
      onDateRangeChange(date);
    }
    setRangeDateTimeText(text);
  };

  const getMetricsData = async () => {
    setLoading(true);
    const params = {
      aiId: botId,
      start_time: dayjs(dateRange[0]).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      end_time: dayjs(dateRange[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
    };
    await getSessionMetrics(params).then((res) => {
      setMetricsSessionData(res.data);
    });

    await getMessageMetrics(params)
      .then((res) => {
        setMetricsMessageData(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      getMetricsData();
    }
  }, [dateRange]);

  const download = () => {
    const fileName = `${t('chat_history.overview.title')}_${botId}`;
    // 创建CSV的头部行
    const header = [t('dashboard.series'), rangeDateText];
    // 将每个数据对象转换为CSV格式的一行
    const rows = cardList.map((item) => {
      if (['scoreCompleteRate', 'answerRate'].includes(item.id)) {
        return [item.title, String(item.value + '%')];
      } else {
        return [item.title, String(item.value)];
      }
    });
    rows.push([t('chat_history.overview.average_score'), averageScore]);

    const data = {
      header,
      rows,
    };
    generateCSV(fileName, data);
  };

  return (
    <Stack gap={0} className="rounded-lg overflow-hidden">
      <section className="p-3 border-b bg-violet-100 rounded-lg">
        <Group justify="space-between">
          <Text fw={400}>{t('chat_history.overview.title')}</Text>
          <Group>
            {loading ? (
              <Loader size="sm" variant="dots" />
            ) : (
              <Text span inherit className="text-violet-500">
                {rangeDateText}
              </Text>
            )}
            <DateSelector dateRange={dateRange} onDateChange={handleDateChange} />
            <Tooltip label={t('chat_history.overview.export')} withArrow>
              <Button onClick={download} size="sm" h={'auto'} p={16} radius={12} disabled={!rangeDateText}>
                <SvgDownload />
              </Button>
            </Tooltip>
          </Group>
        </Group>
      </section>

      <Group className="py-2 items-stretch" wrap="nowrap">
        <SimpleGrid verticalSpacing="sm" cols={4} className="grow">
          {cardList.map((item, index) => {
            return <ItemCard key={index} {...item} time={rangeDateTimeText} />;
          })}
        </SimpleGrid>
        <Card
          shadow="sm"
          p="lg"
          radius="md"
          className="group transform transition-transform duration-300 hover:shadow-md cursor-pointer lg:min-w-[220px]"
        >
          <Stack align="center" justify="center" className="h-full">
            <Group gap={5} wrap="nowrap">
              <Text className="text-gray-900" size="sm">
                {t('chat_history.overview.average_score')}
              </Text>
              <Tooltip label={t('chat_history.overview.average_score_tip')} withinPortal withArrow>
                <span>
                  <SvgInfo />
                </span>
              </Tooltip>
            </Group>

            <Rating readOnly value={+averageScore} fractions={10} />
            <Text className="text-gray-900" size="xl" fw={700}>
              {averageScore}
            </Text>
            <Text className="text-[#98A2B3]" size="sm">
              {rangeDateTimeText}
            </Text>
          </Stack>
        </Card>
      </Group>
    </Stack>
  );
};
